import { FaGithub, FaLinkedin } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { to: "/tutors", label: "Browse Tutors" },
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact" },
  ];

  const socialLinks = [
    {
      name: "GitHub",
      url: "https://github.com/AmmarSAA",
      icon: FaGithub,
      color: "hover:text-gray-400",
    },
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/in/AmmarSAA/",
      icon: FaLinkedin,
      color: "hover:text-blue-400",
    },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-background to-surface border-t border-border/50 mt-20">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <Link to="/" className="inline-block group">
                <h3 className="text-2xl lg:text-3xl font-bold text-primary mb-3 group-hover:opacity-80 transition-opacity">
                  PeerTutors
                </h3>
              </Link>
              <p className="text-text/70 text-sm lg:text-base leading-relaxed max-w-md">
                Connecting students with experienced tutors for personalized
                learning experiences. Empowering education through peer-to-peer
                collaboration.
              </p>
            </div>

            {/* Tagline */}
            <div className="pt-4">
              <p className="text-text/50 text-xs lg:text-sm italic">
                "Learning Together, Growing Together"
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text/90 uppercase tracking-wider mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-text/60 hover:text-primary text-sm lg:text-base transition-colors duration-200 inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary transition-all duration-200"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text/90 uppercase tracking-wider mb-4">
              Connect
            </h4>
            <div className="space-y-3">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 text-text/60 ${social.color} text-sm lg:text-base transition-all duration-200 group`}
                    aria-label={`Visit ${social.name}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-surface border border-border/50 flex items-center justify-center group-hover:border-primary/50 group-hover:bg-primary/5 transition-all duration-200">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{social.name}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Divider with gradient */}
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/30"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="w-12 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-center sm:text-left">
          {/* Copyright */}
          <div className="space-y-1">
            <p className="text-text/50 text-xs lg:text-sm">
              &copy; {currentYear}{" "}
              <span className="font-semibold text-primary">PeerTutors</span>.
              All rights reserved.
            </p>
            <p className="text-text/40 text-xs">
              Developed by{" "}
              <a
                href="https://github.com/AmmarSAA"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary/80 hover:text-primary transition-colors"
              >
                AmmarSAA
              </a>
            </p>
          </div>

          {/* Version / Badge */}
          <div className="flex items-center justify-center sm:justify-end gap-2">
            <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              <span className="text-xs font-medium text-primary">v1.0</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-surface border border-border/30">
              <span className="text-xs font-medium text-text/60">
                Built with React
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
    </footer>
  );
}
